import { memo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipPortal } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { NavItem } from './navigation-config';

interface SidebarNavItemProps {
  item: NavItem;
  isExpanded: boolean;
  onClick: (href: string) => void;
  className?: string;
}

export const SidebarNavItem = memo(function SidebarNavItem({
  item,
  isExpanded,
  onClick,
  className,
}: SidebarNavItemProps) {
  const location = useLocation();
  const isActive = item.isActive(location.pathname);
  const Icon = item.icon;

  const button = (
    <Button
      variant={isActive ? 'default' : 'ghost'}
      className={cn(
        'w-full flex items-center gap-2 transition-all min-h-[44px] md:h-9',
        isExpanded ? 'justify-start px-3' : 'justify-center px-0',
        isActive && 'bg-[#F37022] hover:bg-[#E36012]',
        !isActive && 'dark:text-gray-200 dark:hover:bg-gray-700',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1',
        className
      )}
      onClick={() => onClick(item.href)}
      onMouseEnter={item.onHoverPrefetch}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span
        className={cn(
          'transition-opacity duration-300 text-sm',
          isExpanded ? 'opacity-100' : 'opacity-0 absolute overflow-hidden w-0'
        )}
      >
        {item.name}
      </span>
    </Button>
  );

  // In collapsed mode, wrap with tooltip
  if (!isExpanded) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipPortal>
            <TooltipContent
              side="right"
              className="bg-gray-900 text-white border-0 dark:border-gray-700"
              style={{ zIndex: 99999 }}
            >
              {item.name}
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
});
